import { useEffect, useMemo } from "react";

import { useQuery } from "@apollo/client";
import { Input } from "antd";
import { useDispatch } from "react-redux";

import { RVFormItem } from "@regal-voice/ui";
import { useAudiencePathing } from "Pages/audience/useAudiencePathing";
import { renderErrorMessage } from "Services/LoggingService";
import { getSegmentById } from "Services/marketing-api/segments/queries";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import { SegmentType } from "Types/Segment";

import { unflattenFields } from "../../../Utils/utils";
import { SegmentSelect } from "./SegmentSelect/SegmentSelect";

import localStyles from "./SegmentSelection.module.scss";

export function SegmentSelection({
    formValues,
    setSegmentUploading,
}: {
    formValues: Record<string, any>;
    setSegmentUploading: any;
}): JSX.Element {
    const dispatch = useDispatch();
    const { segmentsBasePath } = useAudiencePathing();

    const { segmentId } = formValues;

    function changeSegment(segmentId: string | undefined, segmentName: string | undefined): void {
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    segmentId: segmentId,
                    segmentName: segmentName,
                })
            )
        );
    }

    const { data, refetch } = useQuery(getSegmentById, {
        variables: { id: segmentId },
        skip: !segmentId,
        errorPolicy: "all",
        fetchPolicy: "network-only",
        onError: (error) => {
            const graphQLErrors = error.graphQLErrors;
            const segmentProbablyDeleted = graphQLErrors && graphQLErrors[0]?.extensions?.code == "BAD_USER_INPUT";
            if (segmentProbablyDeleted) {
                changeSegment(undefined, undefined);
            } else {
                renderErrorMessage({ content: error.message, error, duration: 10 });
            }
        },
    });

    const segmentUploading = useMemo(() => {
        if (data?.getSegment?.type == SegmentType.STATIC) {
            setSegmentUploading(!data?.getSegment?.stats?.finished);
            return !data?.getSegment?.stats?.finished;
        }
        setSegmentUploading(false);
        return false;
    }, [data?.getSegment]);

    useEffect(() => {
        if (!(segmentId && segmentUploading)) {
            return;
        }

        const interval = setInterval(() => {
            refetch();
        }, 10000);

        return () => clearInterval(interval);
    }, [segmentId, segmentUploading]);

    return (
        <>
            <RVFormItem name="segmentName" hidden={true}>
                <Input />
            </RVFormItem>

            <SegmentSelect
                onChange={changeSegment}
                formValues={formValues}
                className={localStyles.segmentField}
                segmentsBasePath={segmentsBasePath}
            />
        </>
    );
}
